import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiEye } from 'react-icons/fi';

import { itemReviewsApi } from '@/services/itemReviews';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Filter, FilterContent, FilterActions, FilterRow } from '@/components/custom/filter';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import GenerateBatchModal from './GenerateBatchModal';
import BatchProgressModal from './BatchProgressModal';

const STATUS_FILTERS = [
  { value: 'em_revisao', label: 'Em revisão' },
  { value: 'aprovado', label: 'Aprovados' },
  { value: 'reprovado', label: 'Reprovados' },
  { value: 'pede_revisao', label: 'Pede revisão' },
  { value: 'all', label: 'Todos' }
];

const ENEM_AREAS = [
  { value: 'LC', label: 'LC' },
  { value: 'MT', label: 'MT' },
  { value: 'CN', label: 'CN' },
  { value: 'CH', label: 'CH' }
];

const ETAPA_FILTERS = [
  { value: 'all', label: 'Todas as etapas' },
  { value: 'serie_3_em', label: '3ª série EM' },
  { value: 'ano_9_ef', label: '9º ano EF' },
  { value: 'ano_5_ef', label: '5º ano EF' },
  { value: 'ano_2_ef', label: '2º ano EF' }
];

const ETAPA_BADGE = {
  serie_3_em: { label: '3ª EM', variant: 'info' },
  ano_9_ef: { label: '9º EF', variant: 'primary' },
  ano_5_ef: { label: '5º EF', variant: 'primary' },
  ano_2_ef: { label: '2º EF', variant: 'primary' }
};

const STATUS_CONFIG = {
  em_revisao: { label: 'Em revisão', variant: 'warning' },
  aprovado: { label: 'Aprovado', variant: 'success' },
  reprovado: { label: 'Reprovado', variant: 'danger' },
  pede_revisao: { label: 'Pede revisão', variant: 'info' }
};

const FALLBACK_STATUS = { label: '—', variant: 'neutral-lightest' };

const TABLE_HEAD = [{ label: 'Código' }, { label: 'Enunciado' }, { label: 'Status', width: 140 }, { label: 'Etapa', width: 100 }, { label: 'Descritor', width: 120 }, { label: 'Atualizado em', width: 170 }, { label: 'Ações', width: 160 }];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

function truncate(text = '', n = 110) {
  return text.length > n ? `${text.slice(0, n - 1)}…` : text;
}

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('em_revisao');
  const [etapaFilter, setEtapaFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [lastBatchId, setLastBatchId] = useState(null);

  const breadcrumbs = {
    title: 'Banco de questões',
    nav: [{ name: 'Revisão pedagógica', isActive: true }]
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = (page = 1) => {
    setLoading(true);
    const filters = {};
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (etapaFilter !== 'all') filters.etapa = etapaFilter;
    if (debouncedSearch) filters.codigo_descritor = debouncedSearch;

    return itemReviewsApi
      .index({ filters, page, perPage: 25 })
      .then(json => {
        const payload = json?.data || [];
        setItems(payload.map(d => ({ id: d.id, ...d.attributes })));
        const meta = json?.meta || {};
        setPagination({
          currentPage: meta.current_page || 1,
          totalPages: meta.total_pages || 1,
          totalCount: meta.total_count || payload.length
        });
      })
      .catch(() => {
        toast.error('Erro ao carregar fila de revisão');
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, etapaFilter, debouncedSearch]);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Status pill tabs */}
      <div className="mb-4">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Filter row */}
      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[200px]">
            <Label htmlFor="etapa-filter">Etapa</Label>
            <select
              id="etapa-filter"
              value={etapaFilter}
              onChange={e => setEtapaFilter(e.target.value)}
              className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
            >
              {ETAPA_FILTERS.map(e => (
                <option
                  key={e.value}
                  value={e.value}
                >
                  {e.label}
                </option>
              ))}
            </select>
          </FilterRow>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="search-code">Buscar</Label>
            <Input
              id="search-code"
              type="search"
              placeholder="Código descritor (ex.: D8, 9N1.3)"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
          <FilterRow>
            <Label>Área ENEM</Label>
            <div className="flex gap-1">
              {ENEM_AREAS.map(area => (
                <button
                  key={area.value}
                  type="button"
                  aria-label={`Filtrar área ${area.label}`}
                  onClick={() => setSearchTerm(prev => (prev === `${area.value}-` ? '' : `${area.value}-`))}
                  className={[
                    'rounded border px-2 py-1 text-xs font-medium transition-colors',
                    searchTerm === `${area.value}-` ? 'border-primary bg-primary text-white' : 'border-neutral-lightest bg-white text-neutral-dark hover:border-primary hover:text-primary'
                  ].join(' ')}
                >
                  {area.label}
                </button>
              ))}
            </div>
          </FilterRow>
        </FilterContent>

        <FilterActions>
          <ButtonGroup>
            <Button
              variant="outline"
              onClick={() => navigate(PATHS.QUESTIONS.REVIEW_MATRIX)}
            >
              Ver matriz
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(PATHS.QUESTIONS.REVIEW_DASHBOARD)}
            >
              Ver dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setLastBatchId(null);
                setProgressOpen(true);
              }}
            >
              Acompanhar batches
            </Button>
            <Button onClick={() => setBatchModalOpen(true)}>Gerar batch</Button>
          </ButtonGroup>
        </FilterActions>
      </Filter>

      {/* Content */}
      {loading ? (
        <Loader />
      ) : (
        <Table className="min-w-[900px]">
          <TableHeaderWrapper tableHead={TABLE_HEAD} />
          <TableBody>
            {items.map(r => {
              const q = r.question || {};
              const cfg = STATUS_CONFIG[r.status] ?? FALLBACK_STATUS;
              const descritor = q.taxonomies?.find(t => /^(D\d+|[259][A-Z]+\d+\.\d+)/.test(t.name))?.name ?? '—';
              const etapaBadge = ETAPA_BADGE[q.etapa] || null;

              return (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs text-neutral">{q.external_id ?? `#${r.id}`}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => navigate(PATHS.QUESTIONS.REVIEW_ITEM(r.id))}
                      className="text-left font-medium text-primary-dark hover:underline"
                    >
                      {truncate(q.description || q.motivator_texts?.[0]?.body, 120)}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant={cfg.variant}>{cfg.label}</Badge>
                  </TableCell>
                  <TableCell>{etapaBadge ? <Badge variant={etapaBadge.variant}>{etapaBadge.label}</Badge> : <span className="text-xs text-neutral-light">—</span>}</TableCell>
                  <TableCell>
                    <Badge variant="neutral-lightest">{descritor}</Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatDate(r.updated_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => navigate(PATHS.QUESTIONS.REVIEW_ITEM(r.id))}
                    >
                      <ButtonIcon>
                        <FiEye />
                      </ButtonIcon>{' '}
                      Revisar
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableNoData
              notFound={items.length === 0}
              colSpan={TABLE_HEAD.length}
            />
          </TableBody>
        </Table>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
            <Button
              key={p}
              size="xs"
              variant={p === pagination.currentPage ? 'default' : 'outline'}
              onClick={() => load(p)}
            >
              {p}
            </Button>
          ))}
        </div>
      )}

      <GenerateBatchModal
        open={batchModalOpen}
        onOpenChange={setBatchModalOpen}
        onSuccess={createdBatch => {
          load(1);
          setLastBatchId(createdBatch?.id || null);
          setProgressOpen(true);
        }}
      />

      <BatchProgressModal
        open={progressOpen}
        onOpenChange={setProgressOpen}
        highlightId={lastBatchId}
      />
    </DashboardContent>
  );
}
